import { makeObservable, computed, observable, action } from 'mobx';
import { BalanceInformationNodeDto } from '../../types';
import { NodeBaseModel, InputBaseModel } from '..';
import { SourceModelBase } from '../../../custom_shared/models';
import { InputFactory, InputIds, InstanceUtils } from '../../misc';
import { WorksheetModel } from '../../models';
import { Regex } from '../../../custom_shared/misc';

export default class BalanceInformationNodeModel extends NodeBaseModel {
    netDue: number = 0;

    inputs: InputBaseModel[] = [];

    constructor(
        data: BalanceInformationNodeDto,
        sources: SourceModelBase[],
        worksheet: WorksheetModel,
        parentNode?: NodeBaseModel
    ) {
        super(data, worksheet, parentNode);

        this.netDue = data.netDue;
        this.inputs = InputFactory.createInputs(this, data.inputs, sources);

        makeObservable(this, {
            netDue: observable,
            setNetDue: action,
            position: computed,
            name: computed,
            netDueLabel: computed,
            entryCodeNodes: computed,
            businessIdInput: computed,
            insuredPeriodStartInput: computed,
            insuredPeriodEndInput: computed,
            underwritingYearInput: computed,
            currencyInput: computed,
            documentSignedShareInput: computed,
            systemSignedShareInput: computed,
            signedShareTypeInput: computed,
            dueDateInput: computed,
            accountingPeriodInput: computed,
            accountingYearInput: computed,
            signedShareValue: computed
        });
    }

    setNetDue(netDue: number) {
        this.netDue = netDue;
    }

    update(data: BalanceInformationNodeDto, sources: SourceModelBase[]) {
        this.netDue = data.netDue;
        this.inputs = InputFactory.createOrUpdateInputs(this, this.inputs, data.inputs, sources);
    }

    get position(): number {
        return this.worksheet.balanceInformationNodes.indexOf(this) + 1;
    }

    get name(): string {
        return `Balance Information #${this.position}`;
    }

    get netDueLabel() {
        return this.netDue.toFixed(2).replace(Regex.decimalRegex, ',');
    }

    get entryCodeNodes() {
        return this.childNodes.filter(InstanceUtils.isEntryCodeNode);
    }

    get businessIdInput() {
        return this.getInputById(InputIds.BalanceInformationNode.BusinessId);
    }

    get insuredPeriodStartInput() {
        return this.getInputById(InputIds.BalanceInformationNode.InsuredPeriodStart);
    }

    get insuredPeriodEndInput() {
        return this.getInputById(InputIds.BalanceInformationNode.InsuredPeriodEnd);
    }

    get underwritingYearInput() {
        return this.getInputById(InputIds.BalanceInformationNode.UnderwritingYear);
    }

    get currencyInput() {
        return this.getInputById(InputIds.BalanceInformationNode.Currency);
    }

    get documentSignedShareInput() {
        return this.getInputById(InputIds.BalanceInformationNode.DocumentSignedShare);
    }

    get systemSignedShareInput() {
        return this.getInputById(InputIds.BalanceInformationNode.SystemSignedShare);
    }

    get signedShareTypeInput() {
        return this.getInputById(InputIds.BalanceInformationNode.SignedShareType);
    }

    get dueDateInput() {
        return this.getInputById(InputIds.BalanceInformationNode.DueDate);
    }

    get accountingPeriodInput() {
        return this.getInputById(InputIds.BalanceInformationNode.AccountingPeriod);
    }

    get accountingYearInput() {
        return this.getInputById(InputIds.BalanceInformationNode.AccountingYear);
    }

    get signedShareValue() {
        return this.signedShareTypeInput?.value === InputIds.BalanceInformationNode.SystemSignedShare
            ? this.systemSignedShareInput?.value
            : this.documentSignedShareInput?.value;
    }

    get dto(): BalanceInformationNodeDto {
        return {
            id: this.id,
            type: this.type,
            netDue: this.netDue,
            inputs: this.inputs.map(input => input.dto),
            childNodes: this.childNodes.map(node => node.dto)
        };
    }
}
